"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  EmojiDetail,
  type EmojiDetailStrings,
} from "@/components/tools/random-fun/emoji-detail";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type EmojiDatabase,
  type EmojiEntry,
  loadEmojiData,
  searchEmoji,
  withTone,
} from "@/lib/tool-kit/random-fun/emoji-data";

/** English strings. A translated content file overrides these through `ui`. */
export const EMOJI_SEARCH_STRINGS = {
  searchLabel: "Search emoji by name or keyword",
  searchPlaceholder: "happy, cat, pizza, heart…",
  groupLabel: "Category",
  groupAll: "All",
  toneLabel: "Skin tone",
  tones: {
    "-1": "Default",
    "0": "Light",
    "1": "Medium-light",
    "2": "Medium",
    "3": "Medium-dark",
    "4": "Dark",
  },
  loading: "Loading the emoji list…",
  loadError:
    "The emoji list couldn't be loaded. Check your connection and try again.",
  retry: "Try again",
  versionNote: "Emoji {version}. Names and keywords from Unicode and the CLDR.",
  results: "Emoji",
  count: "one: {count} emoji|other: {count} emoji",
  showing: "Showing the first {shown}.",
  showMore: "Show more",
  noMatch:
    "No emoji match that search. Try a simpler word, such as smile or dog.",
  tapToCopy: "Click or tap an emoji to copy it and see its details.",
  copiedMessage: "Copied {emoji}",
  failedMessage: "Could not copy. Select the emoji by hand.",
  detailName: "Name",
  detailGroup: "Category",
  detailSince: "Added in",
  detailKeywords: "Keywords",
  detailCodePoints: "Code points",
  detailHtml: "HTML",
  detailCss: "CSS",
  detailJs: "JavaScript",
  copyEmoji: "Copy emoji",
  copyName: "Copy name",
  copyCodePoints: "Copy code points",
  copied: "Copied!",
  none: "None",
  sinceValue: "Emoji {version}",
} as const;

const PAGE = 240;
const TONE_VALUES = ["-1", "0", "1", "2", "3", "4"] as const;

type Load =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; database: EmojiDatabase };

export default function EmojiSearchWidget() {
  const strings = useToolUi(EMOJI_SEARCH_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("all");
  const [tone, setTone] = useState("-1");
  const [limit, setLimit] = useState(PAGE);
  const [selected, setSelected] = useState<EmojiEntry | null>(null);
  const [status, setStatus] = useState("");

  const start = useCallback(() => {
    setLoad({ state: "loading" });
    loadEmojiData().then(
      (database) => setLoad({ state: "ready", database }),
      () => setLoad({ state: "error" })
    );
  }, []);

  useEffect(() => {
    start();
  }, [start]);

  const database = load.state === "ready" ? load.database : null;
  const results = useMemo(
    () =>
      database
        ? searchEmoji(database, query, {
            group: group === "all" ? null : Number(group),
          })
        : [],
    [database, query, group]
  );
  const toneIndex = Number(tone) >= 0 ? Number(tone) : null;

  async function pick(entry: EmojiEntry) {
    const text = withTone(entry, toneIndex);
    setSelected(entry);
    const ok = await writeToClipboard(text);
    setStatus(
      ok ? t(strings.copiedMessage, { emoji: text }) : strings.failedMessage
    );
  }

  const searchField = (
    <TextField
      id="es-search"
      label={strings.searchLabel}
      onChange={(value) => {
        setQuery(value);
        setLimit(PAGE);
      }}
      placeholder={strings.searchPlaceholder}
      type="search"
      value={query}
    />
  );
  if (load.state === "loading") {
    return (
      <div className="space-y-4">
        {searchField}
        <Notice>{strings.loading}</Notice>
      </div>
    );
  }
  if (load.state === "error" || !database) {
    return (
      <div className="space-y-3">
        {searchField}
        <ErrorNotice>{strings.loadError}</ErrorNotice>
        <button className={BUTTON_SECONDARY} onClick={start} type="button">
          {strings.retry}
        </button>
      </div>
    );
  }

  const visible = results.slice(0, limit);
  return (
    <div className="space-y-6">
      {searchField}
      <PillGroup
        label={strings.groupLabel}
        name="es-group"
        onChange={(value) => {
          setGroup(value);
          setLimit(PAGE);
        }}
        options={[
          { value: "all", label: strings.groupAll },
          ...database.groups.map((name, index) => ({
            value: String(index),
            label: name,
          })),
        ]}
        value={group}
      />
      <PillGroup
        label={strings.toneLabel}
        name="es-tone"
        onChange={setTone}
        options={TONE_VALUES.map((value) => ({
          value,
          label: strings.tones[value],
        }))}
        value={tone}
      />
      <output
        aria-live="polite"
        className="block min-h-5 text-foreground text-sm"
      >
        {status}
      </output>
      {selected ? (
        <EmojiDetail
          entry={selected}
          group={database.groups[selected.group] ?? ""}
          strings={strings as EmojiDetailStrings}
          text={withTone(selected, toneIndex)}
        />
      ) : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p className={HINT}>
          {tPlural(strings.count, locale, results.length, {
            count: fmt(results.length),
          })}
          {" · "}
          {strings.tapToCopy}
        </p>
        {results.length === 0 ? (
          <Notice>{strings.noMatch}</Notice>
        ) : (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(3.25rem,1fr))] gap-1">
            {visible.map((entry) => (
              <li key={entry.text}>
                <button
                  aria-label={entry.name}
                  className="flex h-12 w-full items-center justify-center rounded-lg border border-transparent text-2xl transition-colors hover:border-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  onClick={() => pick(entry)}
                  title={entry.name}
                  type="button"
                >
                  {withTone(entry, toneIndex)}
                </button>
              </li>
            ))}
          </ul>
        )}
        {results.length > visible.length ? (
          <div className="space-y-2">
            <p className={HINT}>
              {t(strings.showing, { shown: fmt(visible.length) })}
            </p>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setLimit(limit + PAGE)}
              type="button"
            >
              {strings.showMore}
            </button>
          </div>
        ) : null}
        <p className={HINT}>
          {t(strings.versionNote, { version: database.version })}
        </p>
      </section>
    </div>
  );
}
